import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { Mensagem, Anexo, AISuggestion } from '../../types';
import { FileUpload } from '../FileUpload/FileUpload';
import { AISuggestionsPanel } from '../AISuggestionsPanel';
import { apiService } from '../../services/api';
import './Chat.css';

/** Format seconds as MM:SS */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Custom audio player with play triangle - WhatsApp-style */
const CustomAudioPlayer: React.FC<{ src: string; onError?: () => void }> = ({ src, onError }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(onError);
    }
    setIsPlaying(!isPlaying);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="custom-audio-player">
      <audio ref={audioRef} src={src} onError={onError} preload="metadata" />
      <button type="button" className="custom-audio-play" onClick={togglePlay} aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}>
        {isPlaying ? (
          <div className="custom-audio-pause"><span /><span /></div>
        ) : (
          <div className="custom-audio-triangle" />
        )}
      </button>
      <div className="custom-audio-progress-wrap">
        <div className="custom-audio-progress" style={{ width: `${progress}%` }} />
      </div>
      <span className="custom-audio-duration">
        {duration > 0 ? `${formatDuration(currentTime)} / ${formatDuration(duration)}` : '0:00'}
      </span>
    </div>
  );
};

/** Native audio with URL - shows fallback when load fails (e.g. CORS) */
const AudioWithFallback: React.FC<{ url: string }> = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="message-audio-error-wrap">
        <p className="message-audio-error">Áudio indisponível</p>
        <button type="button" className="message-audio-retry" onClick={() => setFailed(false)}>
          Tentar novamente
        </button>
      </div>
    );
  }
  return <CustomAudioPlayer src={url} onError={() => setFailed(true)} />;
};

/** Audio message that fetches via proxy (auth) - falls back to mediaUrl if proxy fails */
const AudioMessage: React.FC<{
  leadId: string;
  messageId: string;
  mediaUrl?: string | null;
  onRefreshMessages?: () => Promise<void>;
}> = ({ leadId, messageId, mediaUrl, onRefreshMessages }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [useDirectUrl, setUseDirectUrl] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;

    const fetchMedia = (retryCount = 0) => {
      apiService
        .getMediaBlobUrl(leadId, messageId)
        .then((u) => {
          if (!cancelled) {
            url = u;
            setBlobUrl(u);
          }
        })
        .catch((err) => {
          if (retryCount < 2 && (err?.response?.status === 404 || err?.response?.status === 500)) {
            setTimeout(() => fetchMedia(retryCount + 1), 600 + retryCount * 400);
          } else if (mediaUrl) {
            setUseDirectUrl(true);
          } else {
            setError(true);
          }
        });
    };

    fetchMedia();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [leadId, messageId, mediaUrl, retryKey]);

  const [retrying, setRetrying] = useState(false);
  const handleRetry = async () => {
    setRetrying(true);
    setError(false);
    setBlobUrl(null);
    setUseDirectUrl(false);
    try {
      await onRefreshMessages?.();
      setRetryKey((k) => k + 1);
    } finally {
      setRetrying(false);
    }
  };

  if (error) {
    return (
      <div className="message-audio-error-wrap">
        <p className="message-audio-error">Áudio indisponível</p>
        <button type="button" className="message-audio-retry" onClick={handleRetry} disabled={retrying}>
          {retrying ? 'Carregando...' : 'Tentar novamente'}
        </button>
      </div>
    );
  }
  if (useDirectUrl && mediaUrl) return <CustomAudioPlayer src={mediaUrl} onError={() => setError(true)} />;
  if (blobUrl) return <CustomAudioPlayer src={blobUrl} onError={() => setError(true)} />;
  return <p className="message-audio-loading">Carregando áudio...</p>;
};

interface ChatProps {
  leadId: string;
  mensagens: Mensagem[];
  anexos?: Anexo[];
  onSendMensagem: (payload: {
    conteudo?: string;
    tipo?: 'text' | 'image' | 'document' | 'audio';
    mediaUrl?: string;
    s3Key?: string;
    mimeType?: string;
  }) => Promise<void>;
  onAnexoAdded?: (anexo: Anexo) => void;
  aiEnabled?: boolean;
  onRefreshMessages?: () => Promise<void>;
  /** Mostra bolha de “IA a processar” (ex.: POST /ia/start ou orquestração em curso no servidor). */
  iaOrchestrationActive?: boolean;
}

export const Chat: React.FC<ChatProps> = ({
  leadId,
  mensagens,
  anexos = [],
  onSendMensagem,
  onAnexoAdded,
  aiEnabled = false,
  onRefreshMessages,
  iaOrchestrationActive = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  
  // AI Suggestions state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[] | null>(null);
  const [aiReasoning, setAiReasoning] = useState<string | null>(null);
  const [showAiPanel, setShowAiPanel] = useState(false);

  const orderedMensagens = useMemo(
    () => [...mensagens].sort((a, b) => a.timestamp - b.timestamp),
    [mensagens]
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [orderedMensagens, iaOrchestrationActive]);

  const inputLocked = sending || iaOrchestrationActive;

  useEffect(() => {
    return () => {
      if (audioPreviewUrl) {
        URL.revokeObjectURL(audioPreviewUrl);
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [audioPreviewUrl]);

  const handleSend = async () => {
    if (!inputValue.trim() || sending || iaOrchestrationActive) return;

    const conteudo = inputValue.trim();
    setInputValue('');
    setSending(true);

    try {
      await onSendMensagem({ conteudo, tipo: 'text' });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setInputValue(conteudo); // Restaura o texto em caso de erro
      const message =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Não foi possível enviar a mensagem. Verifique a conexão da conta WhatsApp.';
      window.alert(message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleUploadComplete = (anexo: Anexo) => {
    setUploadError(null);
    onAnexoAdded?.(anexo);
  };

  const handleUploadError = (error: string) => {
    setUploadError(error);
    setTimeout(() => setUploadError(null), 5000);
  };

  const stopActiveRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (isRecording || sending || iaOrchestrationActive) return;
    setRecordingError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // WhatsApp/Evolution support: ogg, mp4, mpeg, amr, aac. Prefer these over webm.
      const preferredMimeTypes = [
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4',
        'audio/mpeg',
        'audio/webm;codecs=opus',
        'audio/webm',
      ];
      const selectedMimeType = preferredMimeTypes.find((mime) =>
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)
      );

      const recorder = selectedMimeType
        ? new MediaRecorder(stream, { mimeType: selectedMimeType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const mimeType = recorder.mimeType || selectedMimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          setRecordingError('Não foi possível capturar o áudio. Tente novamente.');
          return;
        }
        if (audioPreviewUrl) {
          URL.revokeObjectURL(audioPreviewUrl);
        }
        setAudioBlob(blob);
        setAudioPreviewUrl(URL.createObjectURL(blob));
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      setRecordingError('Sem permissão de microfone ou dispositivo indisponível.');
      stopActiveRecording();
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    setIsRecording(false);
    stopActiveRecording();
  };

  const uploadBlobToS3 = async (url: string, blob: Blob): Promise<void> => {
    const response = await fetch(url, {
      method: 'PUT',
      body: blob,
      headers: {
        'Content-Type': blob.type || 'audio/webm',
      },
    });
    if (!response.ok) {
      throw new Error(`Falha no upload do áudio (${response.status})`);
    }
  };

  const handleSendRecordedAudio = async () => {
    if (!audioBlob || sending) return;
    setSending(true);
    setRecordingError(null);

    try {
      const mimeType = audioBlob.type || 'audio/webm';
      const baseMime = mimeType.split(';')[0].trim().toLowerCase();
      const extensionMap: Record<string, string> = {
        'audio/ogg': 'ogg',
        'audio/mpeg': 'mp3',
        'audio/mp4': 'm4a',
        'audio/webm': 'webm',
        'audio/wav': 'wav',
      };
      const extension = extensionMap[baseMime] || 'ogg';
      const fileName = `audio-${Date.now()}.${extension}`;
      const { uploadUrl, s3Key } = await apiService.getUploadUrl(leadId, fileName, mimeType);
      await uploadBlobToS3(uploadUrl, audioBlob);

      await onSendMensagem({
        tipo: 'audio',
        conteudo: '[Áudio]',
        s3Key,
        mimeType,
      });

      if (audioPreviewUrl) {
        URL.revokeObjectURL(audioPreviewUrl);
      }
      setAudioPreviewUrl(null);
      setAudioBlob(null);
    } catch (error: any) {
      console.error('Erro ao enviar áudio:', error);
      const backendMsg = error?.response?.data?.message ?? error?.message ?? '';
      const msg = backendMsg || 'Não foi possível enviar o áudio.';
      setRecordingError(msg);
    } finally {
      setSending(false);
    }
  };

  // AI Suggestion handlers
  const handleAISuggest = async () => {
    if (aiLoading || iaOrchestrationActive) return;
    
    setAiLoading(true);
    setShowAiPanel(true);
    setAiSuggestions(null);
    setAiReasoning(null);
    
    try {
      // Enough history so the latest customer request is not dropped after many outbound messages
      const recentMessages = orderedMensagens.slice(-40).map(msg => ({
        messageId: msg.messageId,
        conteudo: msg.conteudo,
        direcao: msg.direcao,
        timestamp: msg.timestamp,
        enviadoPor: msg.enviadoPor,
      }));
      
      const response = await apiService.getAISuggestion(
        leadId,
        recentMessages,
        inputValue || undefined
      );
      
      setAiSuggestions(response.suggestions);
      setAiReasoning(response.reasoning);
    } catch (error) {
      console.error('Erro ao obter sugestão da IA:', error);
      setAiSuggestions([]);
      setAiReasoning('Erro ao gerar sugestões. Tente novamente.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleUseSuggestion = (suggestion: AISuggestion) => {
    setInputValue(suggestion.texto);
    setShowAiPanel(false);
    setAiSuggestions(null);
    setAiReasoning(null);
  };

  const handleCloseAiPanel = () => {
    setShowAiPanel(false);
    setAiSuggestions(null);
    setAiReasoning(null);
    setAiLoading(false);
  };

  const handleDownload = async (anexo: Anexo) => {
    try {
      const { downloadUrl } = await apiService.getDownloadUrl(leadId, anexo.anexoId);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
    }
  };

  /** Prefer WhatsApp/Evolution time (`timestamp`); `createdAt` is DB insert time (same for batch sync). */
  const getMessageDisplayDate = (msg: Mensagem): Date => {
    if (typeof msg.timestamp === 'number' && Number.isFinite(msg.timestamp) && msg.timestamp > 0) {
      return new Date(msg.timestamp);
    }
    const parsed = Date.parse(msg.createdAt);
    if (!Number.isNaN(parsed)) return new Date(parsed);
    return new Date();
  };

  const formatMessageTime = (msg: Mensagem) => {
    const date = getMessageDisplayDate(msg);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageDayKey = (msg: Mensagem): string => {
    const date = getMessageDisplayDate(msg);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatMessageDayLabel = (msg: Mensagem): string => {
    const messageDate = getMessageDisplayDate(msg);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (isSameDay(messageDate, today)) return 'Hoje';
    if (isSameDay(messageDate, yesterday)) return 'Ontem';

    return messageDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'img';
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType.includes('word')) return 'DOC';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'XLS';
    return 'FILE';
  };

  const isLogLike = (content: string | null | undefined): boolean => {
    if (!content || typeof content !== 'string') return false;
    if (content.length > 400) return true;
    const logPatterns = /token=eyJ|WebSocket|wss:\/\/|\.ts:\d+|\.js:\d+|Scheduling reconnect|Connecting to WebSocket/;
    return logPatterns.test(content) || content.split('\n').some((line) => line.length > 120);
  };

  return (
    <div className="chat-container">
      {/* Anexos Section */}
      {anexos.length > 0 && (
        <div className="chat-anexos">
          <div className="anexos-header">
            <span>Anexos ({anexos.length})</span>
          </div>
          <div className="anexos-list">
            {anexos.map((anexo) => (
              <div
                key={anexo.anexoId}
                className="anexo-item"
                onClick={() => handleDownload(anexo)}
                title={`Baixar ${anexo.fileName}`}
              >
                <span className="anexo-icon">{getFileIcon(anexo.mimeType)}</span>
                <span className="anexo-name">{anexo.fileName}</span>
                <span className="anexo-size">{formatFileSize(anexo.fileSize)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="chat-messages">
        {orderedMensagens.length === 0 && !iaOrchestrationActive ? (
          <div className="chat-empty">
            <p>Nenhuma mensagem ainda. Inicie a conversa!</p>
          </div>
        ) : (
          <>
            {orderedMensagens.length === 0 && iaOrchestrationActive && (
              <p className="chat-empty-with-ia">A IA está a preparar a resposta…</p>
            )}
            {orderedMensagens.map((msg, index) => {
              const previous = orderedMensagens[index - 1];
              const showDaySeparator = !previous || getMessageDayKey(previous) !== getMessageDayKey(msg);

              return (
                <React.Fragment key={msg.messageId}>
                  {showDaySeparator && (
                    <div className="chat-day-separator">
                      <span>{formatMessageDayLabel(msg)}</span>
                    </div>
                  )}
                  <div
                    className={`chat-message ${msg.direcao === 'outbound' ? 'outbound' : 'inbound'}`}
                  >
                    <div
                      className={`message-content ${msg.tipo !== 'audio' && !(msg.tipo === 'image' && (msg.mediaUrl || msg.anexoUrl)) && isLogLike(msg.conteudo) ? 'message-log' : ''}`}
                    >
                      {msg.tipo === 'audio' ? (
                        (msg.mediaUrl || msg.anexoUrl) ? (
                          <AudioWithFallback url={(msg.mediaUrl || msg.anexoUrl)!} />
                        ) : msg.s3Key ? (
                          <AudioMessage leadId={leadId} messageId={msg.messageId} onRefreshMessages={onRefreshMessages} />
                        ) : (
                          <div className="message-audio-error-wrap">
                            <p className="message-audio-error">{msg.conteudo || 'Áudio indisponível'}</p>
                            <span className="message-audio-error-hint">Mídia não foi salva. Áudios recebidos precisam ser persistidos no servidor.</span>
                          </div>
                        )
                      ) : msg.tipo === 'image' && (msg.mediaUrl || msg.anexoUrl) ? (
                        <img 
                          src={msg.mediaUrl || msg.anexoUrl!} 
                          alt="Imagem" 
                          className="message-image"
                          onClick={() => window.open(msg.mediaUrl || msg.anexoUrl!, '_blank')}
                        />
                      ) : msg.tipo === 'document' ? (
                        (msg.mediaUrl || msg.anexoUrl) ? (
                          <a
                            href={msg.mediaUrl || msg.anexoUrl!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="message-document-link"
                            download
                          >
                            <span className="message-document-icon">📄</span>
                            {msg.conteudo?.replace(/^\[Documento:\s*|\]$/g, '').trim() || 'Documento'}
                          </a>
                        ) : (
                          <p className="message-document-unavailable">
                            {msg.conteudo}
                            <span className="message-document-hint"> (URL expirada)</span>
                          </p>
                        )
                      ) : (
                        <p>{msg.conteudo}</p>
                      )}
                      <span className="message-time">{formatMessageTime(msg)}</span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            {iaOrchestrationActive && (
              <div className="chat-ia-processing-row" aria-live="polite" aria-busy="true">
                <div className="chat-ia-avatar" title="A gerar resposta com IA">
                  <Sparkles size={20} strokeWidth={2} aria-hidden />
                </div>
                <div className="chat-ia-typing-bubble">
                  <div className="chat-ia-typing-dots" aria-hidden>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {uploadError && (
        <div className="upload-error">
          {uploadError}
        </div>
      )}

      {/* AI Suggestions Panel */}
      {showAiPanel && (
        <AISuggestionsPanel
          suggestions={aiSuggestions || []}
          reasoning={aiReasoning || ''}
          onUseSuggestion={handleUseSuggestion}
          onClose={handleCloseAiPanel}
          loading={aiLoading}
        />
      )}

      {(audioPreviewUrl || recordingError) && (
        <div className="chat-audio-recorder-panel">
          {audioPreviewUrl && (
            <audio controls className="message-audio">
              <source src={audioPreviewUrl} type={audioBlob?.type || 'audio/webm'} />
              Seu navegador não suporta áudio.
            </audio>
          )}
          <div className="chat-audio-actions">
            {recordingError && <span className="chat-audio-error">{recordingError}</span>}
            {audioPreviewUrl && (
              <>
                <button
                  className="chat-audio-send-button"
                  onClick={handleSendRecordedAudio}
                  disabled={sending}
                >
                  {sending ? 'Enviando áudio...' : 'Enviar áudio'}
                </button>
                <button
                  className="chat-audio-discard-button"
                  onClick={() => {
                    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
                    setAudioPreviewUrl(null);
                    setAudioBlob(null);
                    setRecordingError(null);
                  }}
                  disabled={sending}
                >
                  Descartar
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className={`chat-input-container${iaOrchestrationActive ? ' chat-input-container--ia-busy' : ''}`}>
        <div className="chat-left-buttons">
          <FileUpload
            leadId={leadId}
            onUploadComplete={handleUploadComplete}
            onError={handleUploadError}
          />
          <button
            className={`chat-record-button ${isRecording ? 'recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={inputLocked}
            title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
            aria-label={isRecording ? 'Parar gravação' : 'Gravar áudio'}
          >
            {isRecording ? (
              <span className="chat-icon chat-stop-icon" />
            ) : (
              <svg className="chat-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 14a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v4a3 3 0 0 0 3 3zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.92V21h2a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2h2v-3.08A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 0 0 10 0z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
          {aiEnabled && (
            <button
              className="chat-ai-button"
              onClick={handleAISuggest}
              disabled={aiLoading || iaOrchestrationActive}
              title="Sugestões de texto para o atendente (não envia sozinho)"
              aria-label="Sugestões de texto para o atendente"
            >
              {aiLoading ? '...' : (
                <svg className="chat-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 2l1.74 4.35L18 8.09l-4.26 1.74L12 14l-1.74-4.17L6 8.09l4.26-1.74L12 2zm7 8l.87 2.17L22 13l-2.13.83L19 16l-.87-2.17L16 13l2.13-.83L19 10zM5 13l.87 2.17L8 16l-2.13.83L5 19l-.87-2.17L2 16l2.13-.83L5 13z"
                    fill="currentColor"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
        <textarea
          className="chat-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            iaOrchestrationActive ? 'Um momento, por favor…' : 'Digite sua mensagem...'
          }
          rows={2}
          disabled={inputLocked}
          readOnly={iaOrchestrationActive}
        />
        <button
          className="chat-send-button"
          onClick={handleSend}
          disabled={!inputValue.trim() || inputLocked}
        >
          {sending ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  );
};
